/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Financial BI, Gross Margins & Infrastructure Cost DTOs (Task 6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';
import { AdminAnalyticsPeriodSchema } from './admin-analytics.dto';

export const FinancialKpiSummarySchema = z
  .object({
    totalRevenue: z.number().openapi({ description: 'Total revenue (Subscriptions + Token Packs) in USD', example: 45200 }),
    subscriptionRevenue: z.number().openapi({ description: 'Revenue derived from recurring subscriptions', example: 34800 }),
    tokenPackRevenue: z.number().openapi({ description: 'Revenue derived from one-time prepaid token booster packs', example: 10400 }),
    mrr: z.number().openapi({ description: 'Monthly Recurring Revenue run-rate', example: 34800 }),
    arr: z.number().openapi({ description: 'Annualized Run Rate (MRR * 12)', example: 417600 }),
    aiProviderCost: z.number().openapi({ description: 'Total cost incurred from external AI providers', example: 9850 }),
    cloudflareInfraCost: z.number().openapi({ description: 'Estimated Cloudflare Edge infrastructure costs (Workers, D1, R2, Vectorize)', example: 420 }),
    totalCogs: z.number().openapi({ description: 'Cost of Goods Sold (AI APIs + Cloudflare Edge Infra)', example: 10270 }),
    grossProfit: z.number().openapi({ description: 'Net Gross Profit (Revenue - COGS)', example: 34930 }),
    grossMarginPct: z.number().openapi({ description: 'Gross Margin Percentage', example: 77.28 }),
    promotionalCreditsGranted: z.number().openapi({ description: 'Free promotion credits allocated (Signup bonus, referrals, compensations)', example: 1250000 }),
    promotionalCreditsDollarValue: z.number().openapi({ description: 'Estimated dollar liability of promotional credits', example: 1250 }),
  })
  .openapi('FinancialKpiSummary');

export type FinancialKpiSummary = z.infer<typeof FinancialKpiSummarySchema>;

export const FinancialTimelinePointSchema = z
  .object({
    date: z.string().openapi({ description: 'ISO date or month label', example: '2026-10-01' }),
    subscriptionRevenue: z.number().openapi({ example: 1160 }),
    tokenPackRevenue: z.number().openapi({ example: 350 }),
    totalRevenue: z.number().openapi({ example: 1510 }),
    aiCost: z.number().openapi({ example: 320 }),
    infraCost: z.number().openapi({ example: 14 }),
    totalCogs: z.number().openapi({ example: 334 }),
    grossProfit: z.number().openapi({ example: 1176 }),
    grossMarginPct: z.number().openapi({ example: 77.88 }),
  })
  .openapi('FinancialTimelinePoint');

export type FinancialTimelinePoint = z.infer<typeof FinancialTimelinePointSchema>;

export const AiProviderCostItemSchema = z
  .object({
    provider: z.string().openapi({ example: 'anthropic' }),
    providerName: z.string().openapi({ example: 'Anthropic (Claude 3.5 Sonnet)' }),
    estimatedCost: z.number().openapi({ example: 5120.5 }),
    tokensUsed: z.number().openapi({ example: 48500000 }),
    percentageOfAiCost: z.number().openapi({ example: 52.0 }),
  })
  .openapi('AiProviderCostItem');

export type AiProviderCostItem = z.infer<typeof AiProviderCostItemSchema>;

export const CloudflareCostBreakdownSchema = z
  .object({
    workersRequests: z.number().openapi({ example: 12500000 }),
    workersCost: z.number().openapi({ example: 62.5 }),
    d1RowsRead: z.number().openapi({ example: 85000000 }),
    d1RowsWritten: z.number().openapi({ example: 4200000 }),
    d1Cost: z.number().openapi({ example: 85.0 }),
    r2StorageGb: z.number().openapi({ example: 1200 }),
    r2ClassAOps: z.number().openapi({ example: 350000 }),
    r2Cost: z.number().openapi({ example: 28.5 }),
    vectorizeQueries: z.number().openapi({ example: 980000 }),
    vectorizeCost: z.number().openapi({ example: 39.2 }),
    totalInfraCost: z.number().openapi({ example: 215.2 }),
  })
  .openapi('CloudflareCostBreakdown');

export type CloudflareCostBreakdown = z.infer<typeof CloudflareCostBreakdownSchema>;

export const GatewayRevenueShareSchema = z
  .object({
    gatewayCode: z.string().openapi({ example: 'stripe' }),
    gatewayName: z.string().openapi({ example: 'Stripe' }),
    volumeUsd: z.number().openapi({ example: 24500 }),
    transactionCount: z.number().openapi({ example: 420 }),
    sharePct: z.number().openapi({ example: 54.2 }),
  })
  .openapi('GatewayRevenueShare');

export type GatewayRevenueShare = z.infer<typeof GatewayRevenueShareSchema>;

export const PromotionalCreditsBreakdownSchema = z
  .object({
    category: z.string().openapi({ example: 'SIGNUP_BONUS' }),
    description: z.string().openapi({ example: 'New User Registration Bonus (1,000 credits)' }),
    totalCredits: z.number().openapi({ example: 450000 }),
    count: z.number().openapi({ example: 450 }),
    estimatedCost: z.number().openapi({ example: 450.0 }),
  })
  .openapi('PromotionalCreditsBreakdown');

export type PromotionalCreditsBreakdown = z.infer<typeof PromotionalCreditsBreakdownSchema>;

export const AdminFinancialReportResponseSchema = z
  .object({
    period: AdminAnalyticsPeriodSchema,
    currency: z.literal('USD'),
    summary: FinancialKpiSummarySchema,
    timeline: z.array(FinancialTimelinePointSchema),
    aiProviderCosts: z.array(AiProviderCostItemSchema),
    cloudflareCosts: CloudflareCostBreakdownSchema,
    gatewayShares: z.array(GatewayRevenueShareSchema),
    promotionalGrants: z.array(PromotionalCreditsBreakdownSchema),
  })
  .openapi('AdminFinancialReportResponse');

export type AdminFinancialReportResponse = z.infer<typeof AdminFinancialReportResponseSchema>;

